import React, { useMemo, useState } from "react";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import FormWithValidation from "./components/common/FormWithValidation";
import { login as apiLogin } from "./api/auth.api.js";
import { GLYDER_LOGO_URL } from "./brandAssets";
import Input from "./ui/Input";
import { useAuth } from "./useAuth";
import "./LoginPage.css";

const schema = z.object({
  username: z.string().trim().min(1, "Username is required."),
  password: z.string().min(1, "Password is required."),
});

const INACTIVE_ACCOUNT_DETAIL = "User account is inactive";
const UNAVAILABLE_ACCOUNT_MESSAGE =
  "Your account is currently unavailable. Please contact your administrator for access.";
const GENERIC_LOGIN_MESSAGE =
  "Your account has not been set up yet or your credentials are incorrect. Please contact your administrator for access.";

const inputStyle = {
  width: "100%",
  minHeight: 52,
  background: "rgba(7, 17, 26, 0.58)",
  borderColor: "rgba(148, 163, 184, 0.28)",
  color: "#F8FAFC",
  boxShadow: "none",
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState("");
  const [showPasswordHelp, setShowPasswordHelp] = useState(false);
  const defaultValues = useMemo(() => ({ username: "", password: "" }), []);

  const handleLogin = async (values) => {
    setFormError("");
    try {
      const data = await apiLogin(values.username, values.password);

      login(data?.access_token || null, values.username, data?.role || "user");
      navigate("/");
    } catch (err) {
      console.error("Login failed:", err);
      const status = err?.response?.status ?? err?.status;
      const detail = err?.response?.data?.detail ?? err?.detail ?? err?.message;
      if (status === 403 && detail === INACTIVE_ACCOUNT_DETAIL) {
        setFormError(UNAVAILABLE_ACCOUNT_MESSAGE);
      } else if (status === 400 || status === 401 || status === 403) {
        setFormError(GENERIC_LOGIN_MESSAGE);
      } else if (!status) {
        setFormError(
          "Unable to reach the authentication service. Please verify the backend is running."
        );
      } else {
        setFormError(
          err?.message || "Unable to login. Please try again or contact an administrator."
        );
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__mist login-page__mist--one" aria-hidden="true" />
      <div className="login-page__mist login-page__mist--two" aria-hidden="true" />
      <div className="login-page__mist login-page__mist--three" aria-hidden="true" />
      <div className="login-page__glide login-page__glide--one" aria-hidden="true" />
      <div className="login-page__glide login-page__glide--two" aria-hidden="true" />

      <main className="login-page__shell">
        <section className="login-page__brand-panel" aria-label="Glyder platform overview">
          <div className="login-page__brand-content">
            <span className="login-page__eyebrow">Enterprise risk workspace</span>
            <div className="login-page__logo-frame">
              <img className="login-page__logo" src={GLYDER_LOGO_URL} alt="Glyder" />
            </div>
            <p className="login-page__lead">
              Bring risks, controls, policies, and evidence into one calm operating view for
              better decisions and cleaner governance.
            </p>
            <div className="login-page__highlights" aria-hidden="true">
              <span>Executive visibility</span>
              <span>Structured assurance</span>
              <span>Reliable evidence trails</span>
            </div>
          </div>
        </section>

        <section className="login-page__form-panel">
          <div className="login-page__card">
            <span className="login-page__card-kicker">Welcome back</span>
            <h2 className="login-page__card-title">Sign in to Glyder</h2>
            <p className="login-page__card-copy">
              Access your risk and control workspace with your organisation-issued username and password.
            </p>

            <FormWithValidation
              schema={schema}
              defaultValues={defaultValues}
              onSubmit={handleLogin}
              submitLabel="Sign in"
              submittingLabel="Signing in..."
              formError={formError}
              formClassName="login-page__form"
              fieldsClassName="login-page__fields"
              actionsClassName="login-page__actions"
            >
              {({ register, formState }) => (
                <>
                  <div className="login-page__field">
                    <label className="login-page__label" htmlFor="login-email">
                      Username
                    </label>
                    <Input
                      id="login-email"
                      type="text"
                      placeholder="Enter your username"
                      autoComplete="username"
                      inputMode="text"
                      spellCheck={false}
                      ariaLabel="Username"
                      error={formState.errors.username?.message}
                      style={inputStyle}
                      {...register("username")}
                    />
                  </div>

                  <div className="login-page__field">
                    <div className="login-page__field-header">
                      <label className="login-page__label" htmlFor="login-password">
                        Password
                      </label>
                      <button
                        type="button"
                        className="login-page__secondary-link"
                        onClick={() => setShowPasswordHelp((current) => !current)}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      ariaLabel="Password"
                      error={formState.errors.password?.message}
                      style={inputStyle}
                      {...register("password")}
                    />
                  </div>
                </>
              )}
            </FormWithValidation>

            <div className="login-page__support">
              <p className="login-page__support-copy">
                Access is provisioned by your administrator. Use the username assigned to your account.
              </p>
              {showPasswordHelp ? (
                <p className="login-page__support-note" role="status">
                  If you cannot sign in, contact your administrator or internal IT team to reset
                  your password or confirm your account is active.
                </p>
              ) : null}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
